import type { UserCartPriceRow, UserCartPriceRowGroup, UserCartPriceRowSection } from 'components/Pages/UserCartPage/UserCartPriceDetails/types'
import { CHECKOUT_ITEM_TYPE_BEDBANK, CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION, CHECKOUT_ITEM_TYPE_BUNDLE_AND_SAVE, CHECKOUT_ITEM_TYPE_EXPERIENCE, CHECKOUT_ITEM_TYPE_FLIGHT, CHECKOUT_ITEM_TYPE_INSURANCE, CHECKOUT_ITEM_TYPE_LE_HOTEL, CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE, CHECKOUT_ITEM_TYPE_LUXURY_PLUS_SUBSCRIPTION, CHECKOUT_ITEM_TYPE_TRANSFER, CHECKOUT_ITEM_TYPE_CRUISE } from 'constants/checkout'
import type { CartItemData } from 'hooks/UserCart/useAllUserCartItemsMetadata'
import { groupBy, sortBy, sum } from 'lib/array/arrayUtils'
import getObjectKey from 'lib/object/getObjectKey'
import { formatOccupantsShort, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { pluralizeToString } from 'lib/string/pluralize'

export const FIXED_SECTION_NAMES = {
  INSURANCE: 'insurance',
  LUXPLUS: 'luxplus-item',
}

export function getOverallUserCartTotals(itemTotals: App.Checkout.ItemViewTotals | undefined): {
  totalIncludingAllFees: number
  memberTotalIncludingAllFees: number
} {
  if (!itemTotals) return { totalIncludingAllFees: 0, memberTotalIncludingAllFees: 0 }
  const {
    extraGuestSurcharge,
    flightAtolFee,
    flightBaggageTotal,
    flightBookingFee,
    flightCreditFee,
    flightServiceFee,
    propertyFees,
  } = itemTotals.otherFees || {}

  const baseFees = (itemTotals.carHireAddonFees || 0) +
    (itemTotals.carHirePaidOnPickUpFees || 0) +
    (extraGuestSurcharge || 0) +
    (flightAtolFee || 0) +
    (flightBaggageTotal || 0) +
    (flightBookingFee || 0) +
    (flightCreditFee || 0) +
    (flightServiceFee || 0) +
    (propertyFees || 0) +
    (itemTotals.surcharge || 0)
  const publicPrice = baseFees + (itemTotals.price || 0)
  return {
    totalIncludingAllFees: publicPrice,
    memberTotalIncludingAllFees: itemTotals.memberPrice > 0 ? baseFees + (itemTotals.memberPrice || 0) : publicPrice,
  }
}

export function getUserCartItemDisplayTotals(itemTotals: App.Checkout.ItemViewTotals | undefined): {
  publicTotal: number
  memberTotal: number
} {
  const {
    memberTotalIncludingAllFees,
    totalIncludingAllFees,
  } = getOverallUserCartTotals(itemTotals)
  if (!itemTotals) return { publicTotal: 0, memberTotal: 0 }
  if (itemTotals.memberPrice <= 0) {
    return {
      publicTotal: totalIncludingAllFees,
      memberTotal: 0,
    }
  } else {
    return {
      publicTotal: totalIncludingAllFees,
      memberTotal: memberTotalIncludingAllFees,
    }
  }
}

const HOTEL_TYPES = [
  CHECKOUT_ITEM_TYPE_LE_HOTEL,
  CHECKOUT_ITEM_TYPE_BEDBANK,
  CHECKOUT_ITEM_TYPE_BUNDLE_AND_SAVE,
]

const insuranceSection: UserCartPriceRowSection = {
  sectionKey: 'insurance',
  groups: [{
    groupKey: 'insurance-group',
    items: [
      {
        key: 'insurance-heading',
        description: 'Travel protection',
        type: 'subtitle',
      },
      {
        key: 'insurance-label',
        description: 'See pricing in checkout',
      },
    ],
  }],
}

function luxPlusSection(items: Array<CartItemData>): UserCartPriceRowSection {
  const membershipItem = items.find(item => item.itemType === CHECKOUT_ITEM_TYPE_LUXURY_PLUS_SUBSCRIPTION)
  const joinFee = items.find(item => item.itemType === CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE)
  const waivedJoiningFee = !joinFee || joinFee.itemTotals?.price === 0
  const joinFeeRow: UserCartPriceRow = waivedJoiningFee ? {
    key: 'luxplus-waived-join',
    description: 'Special offer: No joining fee',
    type: 'waived',
    price: 0,
    waivedPrice: joinFee?.itemTotals?.value,
  } : {
    type: 'description',
    key: 'luxplus-join',
    description: 'One-off joining fee',
    price: joinFee.itemTotals?.price,
  }
  return {
    sectionKey: 'luxplus',
    groups: [{
      groupKey: 'luxplus-group',
      items: [
        {
          key: 'luxplus-subscription',
          description: 'LuxPlus+ Yearly Membership',
          price: membershipItem?.itemTotals?.price,
          type: 'description',
        },
        joinFeeRow,
      ],
    }],
  }
}

function formattedSubtitle(item: CartItemData) {
  const intl = getCurrentIntl()
  const sep = intl.formatMessage(middleDotSeparatorMessage)
  let subtitle = item.subtitle
  if (item.itemType === 'hotel' || item.itemType === 'bedbankHotel') {
    subtitle += ` ${sep} ${pluralizeToString('night', item.duration || 1)}`
  }
  return subtitle
}

function getExperienceTicketRow(tickets: NonNullable<CartItemData['tickets']>, itemId: string): Array<UserCartPriceRow> {
  return tickets.map(ticket => ({
    key: `${itemId}-${ticket.name}`,
    description: `${ticket.count} x ${ticket.name}`,
    price: ticket.price,
    count: ticket.count,
    type: 'description',
  }))
}

function getPriceWithoutTax(item: CartItemData) {
  if (!item.itemTotals || item.itemTotals?.price < 0) return 0
  return item.itemTotals.price - (item.itemTotals.taxesAndFees || 0) -
    (item.itemTotals.extraGuestSurcharge || 0)
}

function getLuxPlusDiscountItem(totals: CartItemData['itemTotals']): UserCartPriceRow | undefined {
  if (!totals?.memberPrice || totals.memberPrice <= 0) return
  const luxPlusSavings = totals.price - totals.memberPrice
  if (luxPlusSavings) {
    return {
      key: 'luxplus-discount',
      price: -Math.abs(luxPlusSavings),
      description: 'LuxPlus+ member discount',
      type: 'discount',
    }
  }
}

function hotelPricing(data: Array<CartItemData>, sectionKey: string, luxPlusSavingsApply?: boolean): UserCartPriceRowSection {
  const title = data[0].sectionHeading
  const groups: Array<UserCartPriceRowGroup> = Array.from(groupBy(data, item => {
    const { startDate, endDate, offerId } = item
    return getObjectKey({ startDate, endDate, offerId })
  })).flatMap(([objectKey, items]) => {
    const group: UserCartPriceRowGroup = {
      groupKey: 'heading-' + objectKey,
      items: [
        ...(title ? [{ description: title, type: 'subtitle', key: objectKey }] as Array<UserCartPriceRow> : []),
        ...(formattedSubtitle(items[0]) ? [{ key: `dates-${objectKey}`, description: formattedSubtitle(items[0])! }] : []),
      ],
    }

    const rooms = items.map(entry => {
      const innerItems: Array<UserCartPriceRow> = [
        ...(entry.groupHeading ? [{ description: entry.groupHeading, price: getPriceWithoutTax(entry), key: 'title' + entry.itemId, type: 'description' } as UserCartPriceRow] : []),
        ...(entry.occupancy ? [{ description: formatOccupantsShort(entry.occupancy), key: entry.itemId }] : []),
      ]
      if (luxPlusSavingsApply) {
        const luxPlusItem = getLuxPlusDiscountItem(entry.itemTotals)
        if (luxPlusItem) {
          innerItems.push(luxPlusItem)
        }
      }
      return {
        groupKey: `room-${entry.itemId}`,
        items: innerItems,
      }
    })
    return [group, ...rooms]
  })
  return {
    sectionKey,
    groups,
  }
}

function experiencePricing(data: Array<CartItemData>, sectionKey: string, luxPlusSavingsApply?: boolean): UserCartPriceRowSection {
  const title = data[0].sectionHeading
  const groups = Array.from(groupBy(data, item => {
    const { startDate, endDate, offerId } = item
    return getObjectKey({ startDate, endDate, offerId })
  })).flatMap(([objectKey, items]) => {
    const ticketList = items.flatMap(entry => {
      const innerItems: Array<UserCartPriceRow> = [
        ...(entry.tickets ? getExperienceTicketRow(entry.tickets, entry.itemId) : []),
      ]
      if (luxPlusSavingsApply) {
        const luxPlusItem = getLuxPlusDiscountItem(entry.itemTotals)
        if (luxPlusItem) {
          innerItems.push(luxPlusItem)
        }
      }
      return innerItems
    })
    return {
      groupKey: 'heading-' + objectKey,
      items: [
        ...(title ? [{ description: title, type: 'subtitle', key: objectKey }] as Array<UserCartPriceRow> : []),
        ...(formattedSubtitle(items[0]) ? [{ key: `dates-${objectKey}`, description: formattedSubtitle(items[0])! }] : []),
        ...ticketList,
      ],
    } as UserCartPriceRowGroup
  })
  return {
    sectionKey,
    groups,
  }
}

function transferPricing(transfers: Array<CartItemData>, sectionKey: string, luxPlusSavingsApply?: boolean): UserCartPriceRowSection {
  const sortedTransfers = sortBy(transfers, transfer => new Date(transfer.startDate || 0), 'asc')
  const title = sortedTransfers[0].sectionHeading
  const groups: Array<UserCartPriceRowGroup> = Array.from(groupBy(sortedTransfers, item => {
    const { offerId } = item
    return getObjectKey({ offerId })
  })).flatMap(([objectKey, items]) => {
    const group: UserCartPriceRowGroup = {
      groupKey: 'heading-' + objectKey,
      items: [
        ...(title ? [{ description: title, type: 'subtitle', key: objectKey }] as Array<UserCartPriceRow> : []),
        ...(items[0]?.occupancy ? [{ description: pluralizeToString('passenger', sumUpOccupancies([items[0].occupancy])), key: 'heading-subtitle-' + items[0]?.itemId }] : []),
      ],
    }

    const rooms = items.map(entry => {
      const innerItems: Array<UserCartPriceRow> = [
        ...(entry.groupHeading ? [{ description: entry.groupHeading, price: getPriceWithoutTax(entry), key: 'title' + entry.itemId, type: 'description' } as UserCartPriceRow] : []),
        ...(formattedSubtitle(entry) ? [{ key: `dates-${objectKey}`, description: formattedSubtitle(entry)! }] : []),
        ...(entry.maxLuggage ? [{ key: `max-luggage-${objectKey}`, description: `Maximum ${entry.maxLuggage} bags` }] : []),
      ]
      if (luxPlusSavingsApply) {
        const luxPlusItem = getLuxPlusDiscountItem(entry.itemTotals)
        if (luxPlusItem) {
          innerItems.push(luxPlusItem)
        }
      }
      return {
        groupKey: `room-${entry.itemId}`,
        items: innerItems,
      }
    })
    return [group, ...rooms]
  })
  return {
    sectionKey,
    groups,
  }
}

function flightPricing(data: Array<CartItemData>, sectionKey: string, luxPlusSavingsApply?: boolean): UserCartPriceRowSection {
  const groups = data.map((item): UserCartPriceRowGroup => {
    let luxPlusItem: UserCartPriceRow | undefined
    if (luxPlusSavingsApply) {
      luxPlusItem = getLuxPlusDiscountItem(item.itemTotals)
    }

    return {
      groupKey: 'heading-' + sectionKey,
      items: [
        ...(item.sectionHeading ? [{
          description: item.sectionHeading,
          type: 'subtitle' as const,
          key: sectionKey,
        }] : []),
        ...(item.subtitle ? [{
          key: `dates-${item.sectionHeading}`,
          type: 'description' as const,
          description: formattedSubtitle(item)!,
          price: getPriceWithoutTax(item),
        }] : []),
        ...(item.occupancy ? [{
          description: formatOccupantsShort(item.occupancy), key: item.itemId,
        }] : []),
        ...(luxPlusItem ? [luxPlusItem] : []),
      ],
    }
  })
  return {
    sectionKey,
    groups,
  }
}

function merchandisePricing(data: Array<CartItemData>, sectionKey: string): UserCartPriceRowSection {
  const merchandiseGroups = groupBy(data, item => item.sectionId)
  const groups: Array<UserCartPriceRowGroup> = merchandiseGroups.values().map((merchandiseItems): UserCartPriceRowGroup => {
    const mainItem = merchandiseItems[0]
    const totalPrice = sum(merchandiseItems.map(item => item.itemTotals?.price ?? 0))
    const items: Array<UserCartPriceRow> = []
    if (mainItem.sectionHeading) {
      items.push({
        key: mainItem.sectionId,
        description: mainItem.sectionHeading,
        type: 'subtitle',
        // Price will be shown with the subtitle if it exists
        price: mainItem.subtitle ? undefined : totalPrice,
      })
    }
    if (mainItem.subtitle) {
      items.push({
        key: mainItem.sectionId,
        description: mainItem.subtitle,
        type: 'description',
        price: totalPrice,
      })
    }

    return {
      groupKey: mainItem.sectionId,
      items,
    }
  }).toArray()

  return {
    sectionKey,
    groups,
  }
}

function cruisePricing(data: Array<CartItemData>, sectionKey: string, luxPlusSavingsApply?: boolean): UserCartPriceRowSection {
  const title = data[0].sectionHeading
  const groups: Array<UserCartPriceRowGroup> = Array.from(groupBy(data, item => {
    const { startDate, endDate, offerId } = item
    return getObjectKey({ startDate, endDate, offerId })
  })).flatMap(([objectKey, items]) => {
    const group: UserCartPriceRowGroup = {
      groupKey: 'heading-' + objectKey,
      items: [
        ...(title ? [{ description: title, type: 'subtitle', key: objectKey }] as Array<UserCartPriceRow> : []),
        ...(formattedSubtitle(items[0]) ? [{ key: `dates-${objectKey}`, description: formattedSubtitle(items[0])! }] : []),
      ],
    }

    const cabins = items.map(entry => {
      const innerItems: Array<UserCartPriceRow> = [
        ...(entry.groupHeading ? [{ description: entry.groupHeading, price: getPriceWithoutTax(entry), key: 'title' + entry.itemId, type: 'description' } as UserCartPriceRow] : []),
        ...(entry.occupancy ? [{ description: formatOccupantsShort(entry.occupancy), key: entry.itemId }] : []),
      ]
      if (luxPlusSavingsApply) {
        const luxPlusItem = getLuxPlusDiscountItem(entry.itemTotals)
        if (luxPlusItem) {
          innerItems.push(luxPlusItem)
        }
      }
      return {
        groupKey: `cabin-${entry.itemId}`,
        items: innerItems,
      }
    })
    return [group, ...cabins]
  })
  return {
    sectionKey,
    groups,
  }
}

function generalPricing(data: Array<CartItemData>, luxPlusSavingsApply?: boolean): UserCartPriceRowSection | undefined {
  const itemType = data[0].itemType
  if (HOTEL_TYPES.includes(itemType)) {
    return hotelPricing(data, itemType, luxPlusSavingsApply)
  }
  if (itemType === CHECKOUT_ITEM_TYPE_EXPERIENCE) {
    return experiencePricing(data, itemType, luxPlusSavingsApply)
  }
  if (itemType === CHECKOUT_ITEM_TYPE_TRANSFER) {
    return transferPricing(data, itemType, luxPlusSavingsApply)
  }
  if (itemType === CHECKOUT_ITEM_TYPE_FLIGHT) {
    return flightPricing(data, itemType, luxPlusSavingsApply)
  }
  if (itemType === CHECKOUT_ITEM_TYPE_CRUISE) {
    return cruisePricing(data, itemType, luxPlusSavingsApply)
  }

  if (itemType === 'merchandise') {
    return merchandisePricing(data, itemType)
  }
}

const itemOrder: Utils.PartialRecord<App.Checkout.AnyItemType, number> = {
  [CHECKOUT_ITEM_TYPE_LE_HOTEL]: 1,
  [CHECKOUT_ITEM_TYPE_BEDBANK]: 2,
  [CHECKOUT_ITEM_TYPE_BUNDLE_AND_SAVE]: 3,
  [CHECKOUT_ITEM_TYPE_FLIGHT]: 4,
  [CHECKOUT_ITEM_TYPE_EXPERIENCE]: 5,
  [CHECKOUT_ITEM_TYPE_CRUISE]: 6,
  [CHECKOUT_ITEM_TYPE_TRANSFER]: 7,
  [CHECKOUT_ITEM_TYPE_INSURANCE]: 8,
  [CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION]: 9,
  [CHECKOUT_ITEM_TYPE_LUXURY_PLUS_JOINING_FEE]: 10,
  [CHECKOUT_ITEM_TYPE_LUXURY_PLUS_SUBSCRIPTION]: 11,
  'merchandise': 12,
}

export function getPriceListBreakdown(activeCartItems: Array<CartItemData>, luxPlusSavingsApply: boolean): Array<UserCartPriceRowSection> {
  const orderedItems = sortBy(activeCartItems, item => itemOrder[item.itemType], 'asc')
  const groupedItemData = groupBy(orderedItems, item => item.sectionId)

  const result: Array<UserCartPriceRowSection> = []
  groupedItemData.forEach((sectionData, sectionId) => {
    switch (sectionId) {
      case FIXED_SECTION_NAMES.INSURANCE:
        result.push(insuranceSection)
        break
      case FIXED_SECTION_NAMES.LUXPLUS:
        result.push(luxPlusSection(sectionData))
        break
      default:
        const pricing = generalPricing(sectionData, luxPlusSavingsApply)
        if (pricing) {
          result.push(pricing)
        }
        break
    }
  })
  return result
}

const EmptyTotals = {
  totalPrice: 0,
  totalValue: 0,
  totalTaxesAndFees: 0,
  discountValue: 0,
  luxPlusSavings: 0,
  activeCartItems: [],
}

export function getUserCartTotals(cartItemsData: Array<CartItemData>, hasLuxPlusBenefits: boolean) {
  if (!cartItemsData.length) {
    return EmptyTotals
  }

  const activeCartItems = cartItemsData.filter(item => !item.isSavedForLater && !item.isDeleted)
  const totals = activeCartItems.reduce((acc, item) => {
    const price = item.itemTotals?.price || 0
    const memberPrice = item.itemTotals?.memberPrice ?? 0
    const value = item.itemTotals?.value || price
    const taxes = item.itemTotals?.taxesAndFees || 0
    const propertyFees = item.itemTotals?.otherFees?.propertyFees || 0
    const surcharge = item.itemTotals?.surcharge || 0
    const {
      memberTotalIncludingAllFees, totalIncludingAllFees,
    } = getOverallUserCartTotals(item.itemTotals)

    return {
      memberTotalIncludingAllFees: acc.memberTotalIncludingAllFees + memberTotalIncludingAllFees,
      totalIncludingAllFees: acc.totalIncludingAllFees + totalIncludingAllFees,
      regularTotal: acc.regularTotal + price,
      memberTotal: acc.memberTotal + (memberPrice > 0 ? memberPrice : price),
      value: acc.value + value,
      taxesAndFees: acc.taxesAndFees + taxes + propertyFees + surcharge,
    }
  }, { memberTotalIncludingAllFees: 0, totalIncludingAllFees: 0, regularTotal: 0, memberTotal: 0, value: 0, taxesAndFees: 0 })

  const totalPrice = hasLuxPlusBenefits ? totals.memberTotalIncludingAllFees : totals.totalIncludingAllFees

  return {
    totalPrice,
    totalValue: totals.value,
    totalTaxesAndFees: totals.taxesAndFees,
    discountValue: totals.value - totalPrice,
    luxPlusSavings: totals.regularTotal - totals.memberTotal,
    activeCartItems,
  }
}
